import { TFunction } from 'i18next';
import { TDatesRange, TExtendedColumn } from '@sim-admin-frontends/ui-shared';
import { CellProps } from 'react-table';
import {
  App,
  PaymentTransactionsByProcessingStatusFilterInput,
  PaymentType,
  TPaymentData,
  TPaymentsData,
  TPaymentTransaction,
} from '@sim-admin-frontends/data-access';
import { formatTableDateTime } from '@sim-admin-frontends/utils-shared';

import PaymentsTableMenu from '../components/payments/view/PaymentsTableMenu';
import {
  STRIPE_PAYMENTS_DASHBOARD,
  STRIPE_PAYMENTS_DASHBOARD_TESTING,
} from '../constants/Constants';

export const createTableColumns = (t: TFunction, app?: App) => {
  const columns: TExtendedColumn<TPaymentsData[number]>[] = [
    {
      Header: `${t('payments.createdAt')}`,
      accessor: 'createdAt',
      Cell: ({ value }) => formatTableDateTime(value),
      disableSortBy: true,
    },
    {
      Header: `${t('payments.receiver')}`,
      accessor: 'receiver',
      disableSortBy: true,
      Cell: ({ row, value }) => value || row.original.paymentType,
    },
    {
      Header: `${t('payments.id')}`,
      accessor: 'id',
      disableSortBy: true,
      Cell: ({ row, value }) => row.original.identifier || value,
    },
    {
      Header: `${t('payments.amount')}`,
      Cell: ({ row }: CellProps<TPaymentTransaction>) =>
        `${row.original.amount} ${row.original.currency}`,
      disableSortBy: true,
    },
    {
      Header: `${t('payments.paymentStatus')}`,
      accessor: 'paymentStatus',
      disableSortBy: true,
    },
    {
      Header: `${t('payments.paidAt')}`,
      accessor: 'paidAt',
      Cell: ({ value }) => (value ? formatTableDateTime(value) : ''),
      disableSortBy: true,
    },
    {
      Header: `${t('payments.processingStatus')}`,
      accessor: 'processingStatus',
      disableSortBy: true,
    },
    {
      Header: `${t('payments.paymentType')}`,
      accessor: 'paymentType',
      disableSortBy: true,
    },
    {
      Header: '',
      id: '0',
      disableSortBy: true,
      Cell: ({ row }: CellProps<TPaymentTransaction>) => {
        return <PaymentsTableMenu id={row.original.id} app={app} />;
      },
    },
  ];
  return columns;
};

export const getPaymentData = (paymentData: TPaymentData): TPaymentTransaction | null => {
  if (!paymentData) {
    return null;
  }
  const {
    stripePaymentId,
    paymentDetails,
    installation,
    paidAt,
    document,
    paymentType,
    receiver,
    marketItem,
    metadata,
  } = paymentData;

  return {
    ...paymentData,
    stripePaymentId: stripePaymentId ?? undefined,
    paidAt: paidAt ?? '',
    installationUuid: installation?.id ?? '',
    image: document?.links?.self,
    paymentType: paymentType ?? PaymentType.Ticket,
    receiver: receiver ?? '',
    marketItem,
    metadata: metadata ?? undefined,
    orderUuid: paymentDetails?.orderUuid ?? undefined,
    simplicityPlus: paymentData.simplicityPlus ?? false,
    marketPaymentDetails: paymentData.marketPaymentDetails ?? undefined,
    refundDetails: paymentData.refundDetails ?? undefined,
    identifier: paymentData.identifier ?? undefined,
  };
};

export const transformDateRangeValues = (
  values: PaymentTransactionsByProcessingStatusFilterInput,
): TDatesRange => ({
  dateFrom: values.datetimeFrom ? new Date(values.datetimeFrom) : new Date(),
  dateTo: values.datetimeTo ? new Date(values.datetimeTo) : new Date(),
});

export const getStripeUrl = (stripePaymentId: string) => {
  if (process.env.NX_ENVIRONMENT === 'production') {
    return `${STRIPE_PAYMENTS_DASHBOARD}/${stripePaymentId}`;
  }

  return `${STRIPE_PAYMENTS_DASHBOARD_TESTING}/${stripePaymentId}`;
};
